// Таблицата на „Анулирани дейности": ниски редове на един ред. Широк екран —
// # · Анулирано на · Анулирано от · Тип · Дата на документа · Склад ·
// Продукти · Нето · Бруто · „⋯"; лаптоп/таблет — кой анулира е приглушено до
// датата, а датата на документа и складът — до типа; телефон — карти.
// Филтрите са в заглавията (Анулирал · Тип · Склад; на средна ширина
// „Анулирано на" носи Анулирал, а „Тип" — и Склад); заглавията сортират,
// редът „Общо за периода" е за всички показани. ↑/↓ по редовете, Enter
// отваря документа; „⋯" = Преглед · Възстанови.
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { DeletedMovement } from "../../../../api/inventory";
import { fmtDate, fmtDateTime } from "../../components/dateTime";
import { formatMoney } from "../../components/numbers";
import { StatusPill } from "../../components/list/ListBits";
import { RowActionsMenu, type RowAction } from "../../components/list/RowActionsMenu";
import { sectionList, type ColumnSections } from "../../components/filters/useColumnFilters";
import { ReportHead } from "./ReportListBits";
import {
  REPORT_HEAD_CLASS,
  REPORT_HEADER_ROW_CLASS,
  REPORT_OPENABLE_ROW_CLASS,
  REPORT_TOTAL_ROW_CLASS,
  moveRowFocus,
  openableRowProps,
} from "./reportListModel";
import { deletedByOf, type DeletedFilterKey, type DeletedSort, type DeletedSortKey } from "./deletedReportModel";

export type DeletedDensity = "full" | "medium" | "cards";

// Литерални класове — Tailwind сканира само пълни низове
const GRID_CLASS = {
  full: "grid grid-cols-[32px_136px_minmax(110px,1fr)_150px_104px_minmax(120px,1fr)_80px_100px_108px_36px] items-center gap-2.5 px-[18px]",
  medium: "grid grid-cols-[32px_minmax(190px,.9fr)_minmax(220px,1.3fr)_80px_108px_36px] items-center gap-2.5 px-[18px]",
} as const;
// Колоните преди сумите — етикетът „Общо за периода" ги заема
const TOTAL_LABEL_SPAN = { full: "col-span-7", medium: "col-span-4" } as const;

type Props = {
  rows: DeletedMovement[];
  density: DeletedDensity;
  typeLabel: (type: string) => string;
  sort: DeletedSort | null;
  onSort: (key: DeletedSortKey) => void;
  filters: ColumnSections<DeletedFilterKey>;
  onOpen: (movement: DeletedMovement) => void;
  actionsOf: (movement: DeletedMovement) => RowAction[];
  totals: { net: number; gross: number };
  footer?: ReactNode;
};

export const DeletedReportTable = ({
  rows,
  density,
  typeLabel,
  sort,
  onSort,
  filters,
  onOpen,
  actionsOf,
  totals,
  footer,
}: Props) => {
  const { t } = useTranslation();
  const warehouses = (movement: DeletedMovement) => movement.warehouseNames.join(", ") || "—";

  if (density === "cards") {
    return (
      <div onKeyDown={moveRowFocus}>
        {rows.map((movement) => (
          <div
            key={movement.id}
            {...openableRowProps(() => onOpen(movement))}
            className="flex cursor-pointer flex-col gap-1.5 border-t border-shade-1 px-3.5 py-3 outline-none focus-visible:bg-fill"
          >
            <div className="flex items-center gap-2">
              <StatusPill tone="brand">{typeLabel(movement.type)}</StatusPill>
              <span className="min-w-0 flex-1 truncate text-[13px] text-ink-2">{warehouses(movement)}</span>
              <RowActionsMenu actions={actionsOf(movement)} compact />
            </div>
            <div className="flex items-baseline justify-between gap-2.5 text-[12.5px] text-body">
              <span className="min-w-0 truncate">
                {fmtDateTime(movement.deletedAt)} · {deletedByOf(movement)}
              </span>
              <span className="font-sheet text-sm font-semibold text-ink">
                {formatMoney(movement.totalValueWithVat || 0)}
              </span>
            </div>
          </div>
        ))}
        {footer}
        <div className="flex items-baseline justify-between gap-2.5 border-t border-shade-2 bg-fill px-3.5 py-3">
          <span className={REPORT_HEAD_CLASS}>{t("inventoryPage.periodTotal")}</span>
          <span className="font-sheet font-bold text-brand-deep-ink">{formatMoney(totals.gross)}</span>
        </div>
      </div>
    );
  }

  const grid = GRID_CLASS[density];
  const full = density === "full";
  const head = (
    label: string,
    options: { sortKey?: DeletedSortKey; align?: "right"; filters?: ReturnType<typeof sectionList> } = {},
  ) => <ReportHead label={label} sort={sort} onSort={onSort} {...options} />;

  return (
    <div className="overflow-x-auto">
      <div className={full ? "min-w-[1080px]" : "min-w-[720px]"} onKeyDown={moveRowFocus}>
        <div className={`${grid} ${REPORT_HEADER_ROW_CLASS}`}>
          {head("#")}
          {head(t("inventoryPage.deletedColWhen"), {
            sortKey: "deletedAt",
            filters: full ? undefined : sectionList(filters.users),
          })}
          {full && head(t("inventoryPage.deletedColBy"), { sortKey: "by", filters: sectionList(filters.users) })}
          {head(t("inventoryPage.colType"), {
            sortKey: "type",
            filters: full ? sectionList(filters.types) : sectionList(filters.types, filters.warehouses),
          })}
          {full && (
            <>
              {head(t("inventoryPage.deletedColDocDate"), { sortKey: "date" })}
              {head(t("inventoryPage.colWarehouse"), { sortKey: "warehouse", filters: sectionList(filters.warehouses) })}
            </>
          )}
          {head(t("inventoryPage.colProducts"), { sortKey: "products", align: "right" })}
          {full && head(t("inventoryPage.colNet"), { sortKey: "net", align: "right" })}
          {head(t("inventoryPage.colGross"), { sortKey: "gross", align: "right" })}
          <span />
        </div>
        {rows.map((movement, index) => (
          <div
            key={movement.id}
            {...openableRowProps(() => onOpen(movement))}
            className={`${grid} ${REPORT_OPENABLE_ROW_CLASS}`}
          >
            <span className="font-sheet text-faint">{index + 1}</span>
            <span className="flex min-w-0 items-baseline gap-2">
              <span className="shrink-0 whitespace-nowrap font-sheet text-ink-2">{fmtDateTime(movement.deletedAt)}</span>
              {!full && <span className="min-w-0 truncate font-medium text-ink">{deletedByOf(movement)}</span>}
            </span>
            {full && <span className="truncate font-medium text-ink">{deletedByOf(movement)}</span>}
            <span className="flex min-w-0 items-center gap-2">
              <StatusPill tone="brand">{typeLabel(movement.type)}</StatusPill>
              {!full && (
                <span className="min-w-0 truncate text-muted">
                  {fmtDate(movement.date)} · {warehouses(movement)}
                </span>
              )}
            </span>
            {full && (
              <>
                <span className="font-sheet text-ink-2">{fmtDate(movement.date)}</span>
                <span className="truncate text-ink-2">{warehouses(movement)}</span>
              </>
            )}
            <span className="text-right font-sheet tabular-nums text-ink-2">{movement.productsCount ?? 0}</span>
            {full && (
              <span className="text-right font-sheet tabular-nums text-ink-2">
                {formatMoney(movement.totalValueNoVat || 0)}
              </span>
            )}
            <span className="text-right font-sheet font-bold tabular-nums text-ink">
              {formatMoney(movement.totalValueWithVat || 0)}
            </span>
            <RowActionsMenu actions={actionsOf(movement)} dense />
          </div>
        ))}
        {footer}
        {rows.length > 0 && (
          <div className={`${grid} ${REPORT_TOTAL_ROW_CLASS}`}>
            <span className={`${REPORT_HEAD_CLASS} ${TOTAL_LABEL_SPAN[density]}`}>{t("inventoryPage.periodTotal")}</span>
            {full && <span className="text-right font-sheet font-semibold text-ink">{formatMoney(totals.net)}</span>}
            <span className="text-right font-sheet font-bold text-brand-deep-ink">{formatMoney(totals.gross)}</span>
            <span />
          </div>
        )}
      </div>
    </div>
  );
};

export default DeletedReportTable;
